import { Injectable, inject } from '@angular/core';
import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { PatientDocument } from '../../models/patient-document.type';
import { FIREBASE_FIRESTORE } from '../../firebase/firebase.providers';
import { FirebaseAuthService } from '../auth/firebase-auth.service';
import { MembershipService } from '../auth/membership.service';
import { UserProfileService } from '../auth/user-profile.service';
import { FirebaseStorageService } from '../storage/firebase-storage.service';

@Injectable()
export class PatientDocumentService {
  private firestore = inject(FIREBASE_FIRESTORE);
  private auth = inject(FirebaseAuthService);
  private membership = inject(MembershipService);
  private profiles = inject(UserProfileService);
  private storage = inject(FirebaseStorageService);

  private documentsCollection(patientId: string) {
    const businessId = this.membership.requireSelectedBusinessId();
    return collection(
      this.firestore,
      'businesses',
      businessId,
      'patients',
      patientId,
      'documents'
    );
  }

  private documentDoc(patientId: string, documentId: string) {
    const businessId = this.membership.requireSelectedBusinessId();
    return doc(
      this.firestore,
      'businesses',
      businessId,
      'patients',
      patientId,
      'documents',
      documentId
    );
  }

  private storagePath(patientId: string, documentId: string, fileName: string): string {
    const businessId = this.membership.requireSelectedBusinessId();
    return `businesses/${businessId}/patients/${patientId}/documents/${documentId}/${fileName}`;
  }

  async listDocuments(patientId: string): Promise<PatientDocument[]> {
    const snap = await getDocs(
      query(this.documentsCollection(patientId), orderBy('uploadedAt', 'desc'))
    );
    return snap.docs
      .map((documentDoc) => parsePatientDocument(documentDoc.id, documentDoc.data()))
      .filter((document): document is PatientDocument => document != null);
  }

  async uploadDocument(patientId: string, file: File): Promise<PatientDocument> {
    if (!file.type) {
      throw new Error('No se pueden subir carpetas completas.');
    }

    const uid = this.auth.uniqueId();
    if (!uid) {
      throw new Error('Not signed in');
    }

    const documentRef = doc(this.documentsCollection(patientId));
    const documentId = documentRef.id;
    const path = this.storagePath(patientId, documentId, file.name);

    await this.storage.addFile(file, path);

    const uploadedByName = this.uploaderDisplayName();
    const payload = {
      fileName: file.name,
      storagePath: path,
      contentType: file.type || null,
      sizeBytes: file.size,
      uploadedBy: uid,
      uploadedByName: uploadedByName || null,
      uploadedAt: serverTimestamp(),
    };

    try {
      await setDoc(documentRef, payload);
    } catch (err) {
      this.storage.removeFile(path);
      throw err;
    }

    return {
      id: documentId,
      fileName: file.name,
      storagePath: path,
      contentType: file.type || undefined,
      sizeBytes: file.size,
      uploadedBy: uid,
      uploadedByName,
      uploadedAt: Timestamp.now(),
    };
  }

  async deleteDocument(patientId: string, document: PatientDocument): Promise<void> {
    await deleteDoc(this.documentDoc(patientId, document.id));
    this.storage.removeFile(document.storagePath);
  }

  downloadDocument(document: PatientDocument): Promise<string> {
    return this.storage.download(document.storagePath);
  }

  private uploaderDisplayName(): string {
    const profile = this.profiles.profile();
    const fromProfile = [profile?.name, profile?.last_name]
      .filter((part) => part?.trim())
      .join(' ')
      .trim();
    if (fromProfile) {
      return fromProfile;
    }
    return this.auth.user()?.email || this.auth.user()?.displayName || '';
  }
}

function parsePatientDocument(
  id: string,
  data: Record<string, unknown>
): PatientDocument | null {
  const fileName = typeof data['fileName'] === 'string' ? data['fileName'] : '';
  const storagePath = typeof data['storagePath'] === 'string' ? data['storagePath'] : '';
  const uploadedBy = typeof data['uploadedBy'] === 'string' ? data['uploadedBy'] : '';
  const uploadedAt = data['uploadedAt'];

  if (!fileName || !storagePath || !uploadedBy || !(uploadedAt instanceof Timestamp)) {
    return null;
  }

  return {
    id,
    fileName,
    storagePath,
    contentType: typeof data['contentType'] === 'string' ? data['contentType'] : undefined,
    sizeBytes: typeof data['sizeBytes'] === 'number' ? data['sizeBytes'] : undefined,
    uploadedBy,
    uploadedByName:
      typeof data['uploadedByName'] === 'string' ? data['uploadedByName'] : undefined,
    uploadedAt,
  };
}
